import { QRCodeSVG } from "qrcode.react";
import { QrCode, Coins, Gift, ScanSmiley, Receipt, Users, ArrowUp, House, Bell, Storefront, WifiHigh, CellSignalFull, BatteryFull } from "@phosphor-icons/react";

const merchants = [
  { name: "Blinka's Coffee", initials: "BC", points: 240, note: "Free Coffee at 300 pts", ready: false },
  { name: "Ecowater", initials: "EW", points: 530, note: "50 pts earned this week", ready: true },
  { name: "ABC Pharmacy", initials: "AB", points: 80, note: "No reward available", ready: false },
];

export function PhoneFrame({ children, className = "" }) {
  return (
    <div className={`relative mx-auto w-[290px] rounded-[2.6rem] bg-[hsl(160,42%,8%)] p-3 shadow-[0_30px_80px_-20px_rgba(16,48,34,0.5)] ${className}`}>
      <div className="absolute top-3 left-1/2 -translate-x-1/2 h-5 w-28 rounded-b-2xl bg-[hsl(160,42%,8%)] z-10" />
      <div className="rounded-[2rem] bg-background overflow-hidden h-[560px]">{children}</div>
    </div>
  );
}

export function CustomerAppScreen() {
  return (
    <div className="h-full flex flex-col bg-secondary/30">
      {/* status bar */}
      <div className="flex items-center justify-between px-5 pt-3 pb-1 text-[10px] font-semibold text-white bg-[hsl(160,42%,8%)]">
        <span className="pl-1">9:41</span>
        <div className="flex items-center gap-1">
          <CellSignalFull size={12} weight="fill" />
          <WifiHigh size={12} weight="fill" />
          <BatteryFull size={14} weight="fill" />
        </div>
      </div>

      {/* green hero header */}
      <div className="bg-[hsl(160,42%,8%)] px-5 pb-6 pt-2 rounded-b-3xl relative overflow-hidden">
        <div className="absolute -right-6 -top-6 h-28 w-28 rounded-full bg-primary/20 blur-2xl" />
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/25 flex items-center justify-center font-display font-bold text-primary">S</div>
            <div>
              <p className="text-[9px] text-white/60">Welcome back</p>
              <p className="font-display font-bold text-white leading-tight">Steven</p>
            </div>
          </div>
          <div className="relative h-8 w-8 rounded-full bg-white/10 flex items-center justify-center">
            <Bell size={16} className="text-white" />
            <span className="absolute top-1.5 right-2 h-1.5 w-1.5 rounded-full bg-primary" />
          </div>
        </div>

        {/* QR identity card */}
        <div className="relative mt-4 bg-white rounded-2xl p-3 flex items-center justify-between shadow-lg">
          <div>
            <p className="text-primary font-mono text-[8px] tracking-widest uppercase">OneKite ID</p>
            <p className="font-display font-bold text-sm text-foreground tabular-nums">207 019 417 6</p>
            <p className="text-[8px] text-muted-foreground mt-1.5 flex items-center gap-1"><QrCode size={10} /> Tap to show your QR</p>
          </div>
          <div className="p-1 bg-white rounded-lg">
            <QRCodeSVG value="OneKite-demo-identity" size={52} bgColor="#ffffff" fgColor="#07110c" level="M" />
          </div>
        </div>

        {/* stat pills */}
        <div className="relative mt-3 grid grid-cols-2 gap-2">
          <div className="bg-white/10 rounded-xl px-3 py-2">
            <p className="font-display font-bold text-white text-lg leading-none">3</p>
            <p className="text-[8px] text-white/60 mt-0.5">Businesses joined</p>
          </div>
          <div className="bg-white/10 rounded-xl px-3 py-2">
            <p className="font-display font-bold text-primary text-lg leading-none">2</p>
            <p className="text-[8px] text-white/60 mt-0.5">Rewards available</p>
          </div>
        </div>
      </div>

      {/* scrollable body */}
      <div className="flex-1 overflow-hidden px-4 pt-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">My Businesses</p>
          <span className="text-[9px] text-primary font-medium">Discover</span>
        </div>
        <div className="space-y-2">
          {merchants.map((m) => (
            <div key={m.name} className="bg-card border border-black/[0.06] rounded-2xl p-2.5 flex items-center gap-3 shadow-sm">
              <div className="h-9 w-9 rounded-xl bg-primary/15 flex items-center justify-center font-display font-bold text-primary text-[11px]">{m.initials}</div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold truncate">{m.name}</p>
                <p className={`text-[9px] ${m.ready ? "text-primary" : "text-muted-foreground"}`}>{m.note}</p>
              </div>
              <div className="text-right">
                <p className="font-display font-bold text-primary text-sm leading-none">{m.points}</p>
                <p className="text-[8px] text-muted-foreground">points</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* bottom tab bar */}
      <div className="relative bg-card border-t border-black/[0.06] px-6 pt-2 pb-3 flex items-end justify-between">
        <TabItem icon={House} label="Home" active />
        <TabItem icon={Storefront} label="Businesses" />
        <div className="flex flex-col items-center -mt-6">
          <div className="h-11 w-11 rounded-full bg-primary flex items-center justify-center shadow-[0_8px_20px_-4px_rgba(16,185,129,0.6)] ring-4 ring-card">
            <QrCode size={20} weight="fill" className="text-primary-foreground" />
          </div>
          <span className="text-[8px] text-muted-foreground mt-1">My QR</span>
        </div>
        <TabItem icon={Bell} label="Alerts" />
        <TabItem icon={Users} label="Profile" />
      </div>
    </div>
  );
}

function TabItem({ icon: Icon, label, active }) {
  return (
    <div className="flex flex-col items-center gap-0.5">
      <Icon size={18} weight={active ? "fill" : "regular"} className={active ? "text-primary" : "text-muted-foreground"} />
      <span className={`text-[8px] ${active ? "text-primary font-medium" : "text-muted-foreground"}`}>{label}</span>
    </div>
  );
}

export function StaffAppScreen() {
  const actions = [
    { icon: ScanSmiley, label: "Scan Customer" },
    { icon: Coins, label: "Award Points" },
    { icon: Gift, label: "Redeem Reward" },
    { icon: Receipt, label: "Today's Activity" },
  ];
  return (
    <div className="p-5 h-full flex flex-col">
      <p className="text-[10px] text-muted-foreground">Blinka's Coffee · Masaki</p>
      <h4 className="font-display font-bold text-lg mb-4">Quick actions</h4>
      <div className="grid grid-cols-2 gap-3 flex-1 content-start">
        {actions.map((a) => (
          <div key={a.label} className="card-surface aspect-square flex flex-col items-center justify-center gap-2">
            <div className="h-10 w-10 rounded-2xl bg-primary/15 flex items-center justify-center">
              <a.icon size={22} weight="duotone" className="text-primary" />
            </div>
            <span className="text-[10px] font-medium text-center px-1">{a.label}</span>
          </div>
        ))}
      </div>
      <div className="card-surface p-3 mt-3 grid grid-cols-3 text-center">
        <div><p className="font-display font-bold text-primary">18</p><p className="text-[8px] text-muted-foreground">Customers</p></div>
        <div><p className="font-display font-bold text-primary">420</p><p className="text-[8px] text-muted-foreground">Points</p></div>
        <div><p className="font-display font-bold text-primary">6</p><p className="text-[8px] text-muted-foreground">Redeemed</p></div>
      </div>
    </div>
  );
}

export function DashboardMockup() {
  const stats = [
    { icon: Users, label: "Customers Today", value: "128" },
    { icon: ArrowUp, label: "Returning Customers", value: "74" },
    { icon: Coins, label: "Points Issued", value: "3,240" },
    { icon: Gift, label: "Rewards Redeemed", value: "19" },
  ];
  const bars = [40, 65, 52, 80, 60, 92, 70];
  return (
    <div className="rounded-2xl bg-background border border-black/10 shadow-[0_30px_80px_-24px_rgba(16,48,34,0.35)] overflow-hidden w-full max-w-md">
      <div className="flex items-center gap-1.5 px-4 py-3 bg-secondary/60 border-b border-black/5">
        <span className="h-2.5 w-2.5 rounded-full bg-destructive/60" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-400/70" />
        <span className="h-2.5 w-2.5 rounded-full bg-primary/60" />
        <span className="ml-3 text-[10px] text-muted-foreground font-mono">OneKite Business · Dashboard</span>
      </div>
      <div className="p-5">
        <div className="grid grid-cols-2 gap-3 mb-4">
          {stats.map((s) => (
            <div key={s.label} className="card-surface p-3.5">
              <s.icon size={18} weight="duotone" className="text-primary mb-1.5" />
              <p className="font-display font-bold text-xl text-foreground">{s.value}</p>
              <p className="text-[10px] text-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>
        <div className="card-surface p-4">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono mb-3">Points issued · last 7 days</p>
          <div className="flex items-end gap-2 h-24">
            {bars.map((h, i) => (
              <div key={i} className="flex-1 rounded-t-md bg-primary/80" style={{ height: `${h}%` }} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
